import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import QuestionTypeSelector, {
  QuestionType,
} from "../QuestionTypeSelector";
import QuestionCard, { PollQuestionData, QuestionOption } from "../QuestionCard";
import { pollsApi, flattenPollQuestionIds } from "../../services/polls.api";

function GripVerticalIcon({
  width = 16,
  height = 16,
}: {
  width?: number;
  height?: number;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="8" cy="6" r="1.25" fill="currentColor" />
      <circle cx="8" cy="10" r="1.25" fill="currentColor" />
      <circle cx="8" cy="14" r="1.25" fill="currentColor" />
      <circle cx="12" cy="6" r="1.25" fill="currentColor" />
      <circle cx="12" cy="10" r="1.25" fill="currentColor" />
      <circle cx="12" cy="14" r="1.25" fill="currentColor" />
    </svg>
  );
}
import styles from "./PollQuestionsStep.module.scss";

interface PollQuestionsStepProps {
  questions: PollQuestionData[];
  onQuestionsChange: (questions: PollQuestionData[]) => void;
  /** Düzenleme modunda verilirse, sürükle-bırak sonrası BE'ye reorder isteği
   *  atılır. Yaratma modunda omit'lenir; sıralama 'Kaydet'te kalıcı olur. */
  surveyId?: string;
  /** Server'da kayıtlı soru ID'leri. Yeni eklenen soru varsa reorder BE'ye
   *  gönderilmez (BE tüm aktif ID'leri istiyor). */
  serverQuestionIds?: Set<string>;
  className?: string;
}

const createDefaultOptions = (type: QuestionType): QuestionOption[] => {
  if (type === "matrix") {
    return ["İyi", "Orta", "İyi Değil"].map((value, i) => ({
      id: `option-${Date.now()}-${i + 1}`,
      value,
    }));
  }
  if (["dropdown", "single_choice", "multiple_choice"].includes(type)) {
    return [
      { id: `option-${Date.now()}-1`, value: "" },
      { id: `option-${Date.now()}-2`, value: "" },
    ];
  }
  return [];
};

const PollQuestionsStep: React.FC<PollQuestionsStepProps> = ({
  questions,
  onQuestionsChange,
  surveyId,
  serverQuestionIds,
  className = "",
}) => {
  const { t } = useTranslation();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleAddQuestion = (type: QuestionType) => {
    const newQuestion: PollQuestionData = {
      id: `question-${Date.now()}`,
      type,
      questionText: "",
      options: createDefaultOptions(type),
      required: false,
      ...(type === "matrix"
        ? { rows: [{ id: `row-${Date.now()}`, value: "" }] }
        : {}),
    };
    onQuestionsChange([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (updatedQuestion: PollQuestionData) => {
    const updatedQuestions = questions.map((q) =>
      q.id === updatedQuestion.id ? updatedQuestion : q
    );
    onQuestionsChange(updatedQuestions);
  };

  const handleDeleteQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter((q) => q.id !== questionId);
    onQuestionsChange(updatedQuestions);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (
    index: number,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (
    targetIndex: number,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (fromIndex === null || fromIndex === targetIndex) return;

    const reordered = [...questions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    onQuestionsChange(reordered);

    if (!surveyId) return;
    // Tablo sorusu sunucuda satır başına bir soru: reorder'a satır id'leriyle gider.
    const orderedIds = flattenPollQuestionIds(reordered);
    const allOnServer =
      serverQuestionIds != null &&
      orderedIds.length === serverQuestionIds.size &&
      orderedIds.every((id) => serverQuestionIds.has(id));
    if (!allOnServer) return;
    try {
      await pollsApi.reorderQuestions(surveyId, orderedIds);
    } catch (err: unknown) {
      const statusCode = (err as { response?: { status?: number } })?.response
        ?.status;
      const errorCode = (
        err as {
          response?: { data?: { code?: string; errorCode?: string } };
        }
      )?.response?.data;
      const code = errorCode?.code ?? errorCode?.errorCode;
      if (statusCode === 404) {
        toast.error(
          t("createPoll.questions.reorderNotFound"),
        );
      } else if (
        statusCode === 400 &&
        code === "SURVEY_QUESTIONS_REORDER_MISMATCH"
      ) {
        toast.error(
          t("createPoll.questions.reorderMismatch"),
        );
      } else {
        toast.error(
          t("createPoll.questions.reorderError"),
        );
      }
    }
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.leftColumn}>
        <QuestionTypeSelector onSelectType={handleAddQuestion} />
      </div>

      <div className={styles.rightColumn}>
        {questions.length === 0 ? (
          <div className={styles.emptyState}>
            {t("createPoll.questions.emptyState")}
          </div>
        ) : (
          <div className={styles.questionsList}>
            {questions.map((question, index) => (
              <div
                key={question.id}
                className={`${styles.questionItem} ${draggedIndex === index ? styles.questionItemDragging : ""} ${dragOverIndex === index ? styles.questionItemDragOver : ""}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(index, e)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(index, e)}
              >
                <span
                  className={styles.dragHandle}
                  aria-label={t("createPoll.questions.dragHandle")}
                  title={t("createPoll.questions.dragHandle")}
                >
                  <GripVerticalIcon width={16} height={16} />
                </span>
                <QuestionCard
                  question={question}
                  onUpdate={handleUpdateQuestion}
                  onDelete={handleDeleteQuestion}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PollQuestionsStep;
export type { PollQuestionData, QuestionOption };
